import { ROLES, normalizeRole } from "./roles";

export const OPS_PERMISSIONS = {
  DASHBOARD_VIEW: "dashboard.view",
  INSTITUTIONS_VIEW: "institutions.view",
  INSTITUTIONS_CREATE: "institutions.create",
  INSTITUTIONS_UPDATE: "institutions.update",
  INSTITUTIONS_ONBOARD: "institutions.onboard",
  INSTITUTIONS_INVITE_RESEND: "institutions.invite_resend",
  ASSIGNMENT_VIEW: "assignment.view",
  ASSIGNMENT_MANAGE: "assignment.manage",
  BATCHES_VIEW: "batches.view",
  CASES_VIEW: "cases.view",
  CASES_VIEW_ASSIGNED: "cases.view_assigned",
  CASES_UPDATE: "cases.update",
  CASES_ASSIGN: "cases.assign",
  CASES_ESCALATE: "cases.escalate",
  CASES_LOG_INTERACTION: "cases.log_interaction",
  CUSTOMERS_VIEW: "customers.view",
  CUSTOMERS_UPDATE: "customers.update",
  CONTACT_CENTRE_VIEW: "contact_centre.view",
  CONTACT_CENTRE_MANAGE: "contact_centre.manage",
  COMMUNICATIONS_VIEW: "communications.view",
  PAYMENTS_VIEW: "payments.view",
  PAYMENTS_INITIATE: "payments.initiate",
  SETTLEMENTS_VIEW: "settlements.view",
  SETTLEMENTS_MANAGE: "settlements.manage",
  ESCALATIONS_VIEW: "escalations.view",
  ESCALATIONS_MANAGE: "escalations.manage",
  LEGAL_VIEW: "legal.view",
  LEGAL_MANAGE: "legal.manage",
  OPS_USERS_VIEW: "ops_users.view",
  OPS_USERS_INVITE: "ops_users.invite",
  OPS_USERS_MANAGE: "ops_users.manage",
  SETTINGS_VIEW: "settings.view",
  SETTINGS_MANAGE: "settings.manage",
  AUDIT_VIEW: "audit.view",
  REPORTS_VIEW: "reports.view",
};

export const ALL_OPS_PERMISSIONS = Object.values(OPS_PERMISSIONS);

export const OPS_PERMISSION_GROUPS = [
  {
    label: "Dashboard",
    permissions: [{ key: OPS_PERMISSIONS.DASHBOARD_VIEW, label: "View ops overview" }],
  },
  {
    label: "Institutions",
    permissions: [
      { key: OPS_PERMISSIONS.INSTITUTIONS_VIEW, label: "View institutions" },
      { key: OPS_PERMISSIONS.INSTITUTIONS_CREATE, label: "Create institutions" },
      { key: OPS_PERMISSIONS.INSTITUTIONS_UPDATE, label: "Update institutions" },
      { key: OPS_PERMISSIONS.INSTITUTIONS_ONBOARD, label: "Onboard institutions" },
      { key: OPS_PERMISSIONS.INSTITUTIONS_INVITE_RESEND, label: "Resend portal invites" },
    ],
  },
  {
    label: "Case assignment",
    permissions: [
      { key: OPS_PERMISSIONS.ASSIGNMENT_VIEW, label: "View institution cases for assignment" },
      { key: OPS_PERMISSIONS.ASSIGNMENT_MANAGE, label: "Assign / reassign cases" },
    ],
  },
  {
    label: "Cases",
    permissions: [
      { key: OPS_PERMISSIONS.CASES_VIEW, label: "View all cases" },
      { key: OPS_PERMISSIONS.CASES_VIEW_ASSIGNED, label: "View assigned cases only" },
      { key: OPS_PERMISSIONS.CASES_UPDATE, label: "Update cases" },
      { key: OPS_PERMISSIONS.CASES_ASSIGN, label: "Assign call centre agents" },
      { key: OPS_PERMISSIONS.CASES_ESCALATE, label: "Escalate cases" },
      { key: OPS_PERMISSIONS.CASES_LOG_INTERACTION, label: "Log interactions" },
    ],
  },
  {
    label: "Customers & contact",
    permissions: [
      { key: OPS_PERMISSIONS.CUSTOMERS_VIEW, label: "View customers" },
      { key: OPS_PERMISSIONS.CUSTOMERS_UPDATE, label: "Update customers" },
      { key: OPS_PERMISSIONS.CONTACT_CENTRE_VIEW, label: "View contact centre" },
      { key: OPS_PERMISSIONS.CONTACT_CENTRE_MANAGE, label: "Manage contact centre" },
      { key: OPS_PERMISSIONS.PAYMENTS_VIEW, label: "View payments" },
      { key: OPS_PERMISSIONS.PAYMENTS_INITIATE, label: "Initiate payment for a case" },
    ],
  },
  {
    label: "Settlements",
    permissions: [
      { key: OPS_PERMISSIONS.SETTLEMENTS_VIEW, label: "View settlement requests" },
      { key: OPS_PERMISSIONS.SETTLEMENTS_MANAGE, label: "Create / manage settlements" },
    ],
  },
  {
    label: "Escalations & legal",
    permissions: [
      { key: OPS_PERMISSIONS.ESCALATIONS_VIEW, label: "View escalations" },
      { key: OPS_PERMISSIONS.ESCALATIONS_MANAGE, label: "Manage escalations" },
      { key: OPS_PERMISSIONS.LEGAL_VIEW, label: "View legal cases" },
      { key: OPS_PERMISSIONS.LEGAL_MANAGE, label: "Manage legal cases" },
    ],
  },
  {
    label: "Team & settings",
    permissions: [
      { key: OPS_PERMISSIONS.OPS_USERS_VIEW, label: "View ops portal users" },
      { key: OPS_PERMISSIONS.OPS_USERS_INVITE, label: "Invite ops users" },
      { key: OPS_PERMISSIONS.OPS_USERS_MANAGE, label: "Manage ops users" },
      { key: OPS_PERMISSIONS.SETTINGS_VIEW, label: "View settings" },
      { key: OPS_PERMISSIONS.SETTINGS_MANAGE, label: "Manage settings" },
      { key: OPS_PERMISSIONS.AUDIT_VIEW, label: "View audit trail" },
    ],
  },
];

const P = OPS_PERMISSIONS;

const ROLE_PERMISSION_MAP = {
  [ROLES.SUPER_ADMIN]: ALL_OPS_PERMISSIONS,
  // Same surface as super admin except ops user RBAC management (mirrors backend seed).
  [ROLES.EXECUTIVE_MANAGER]: ALL_OPS_PERMISSIONS.filter((key) => key !== P.OPS_USERS_MANAGE),
  [ROLES.CALL_CENTER_MANAGER]: [
    P.DASHBOARD_VIEW,
    P.REPORTS_VIEW,
    P.INSTITUTIONS_VIEW,
    P.ASSIGNMENT_VIEW,
    P.ASSIGNMENT_MANAGE,
    P.CASES_VIEW,
    P.CASES_UPDATE,
    P.CASES_ASSIGN,
    P.CASES_ESCALATE,
    P.CASES_LOG_INTERACTION,
    P.CUSTOMERS_VIEW,
    P.CUSTOMERS_UPDATE,
    P.CONTACT_CENTRE_VIEW,
    P.CONTACT_CENTRE_MANAGE,
    P.PAYMENTS_VIEW,
    P.PAYMENTS_INITIATE,
    P.SETTLEMENTS_VIEW,
    P.SETTLEMENTS_MANAGE,
    P.ESCALATIONS_VIEW,
    P.ESCALATIONS_MANAGE,
    P.SETTINGS_VIEW,
    P.AUDIT_VIEW,
  ],
  [ROLES.CALL_CENTER_AGENT]: [
    P.DASHBOARD_VIEW,
    P.REPORTS_VIEW,
    P.CASES_VIEW_ASSIGNED,
    P.CASES_LOG_INTERACTION,
    P.CASES_ESCALATE,
    P.CUSTOMERS_VIEW,
    P.CONTACT_CENTRE_VIEW,
    P.PAYMENTS_VIEW,
    P.PAYMENTS_INITIATE,
    P.SETTINGS_VIEW,
    P.AUDIT_VIEW,
  ],
  [ROLES.LEGAL_OFFICER]: [
    P.DASHBOARD_VIEW,
    P.CASES_VIEW,
    P.PAYMENTS_VIEW,
    P.SETTLEMENTS_VIEW,
    P.ESCALATIONS_VIEW,
    P.LEGAL_VIEW,
    P.LEGAL_MANAGE,
    P.SETTINGS_VIEW,
    P.AUDIT_VIEW,
  ],
};

export function getPermissionsForRole(role) {
  return ROLE_PERMISSION_MAP[normalizeRole(role)] ?? [P.DASHBOARD_VIEW, P.SETTINGS_VIEW, P.AUDIT_VIEW];
}

/** Flatten API permissions (array or module-keyed object) into permission name keys. */
export function flattenPermissionKeys(permissions) {
  if (Array.isArray(permissions)) {
    return permissions.filter((p) => typeof p === "string" && p.includes("."));
  }
  if (permissions && typeof permissions === "object") {
    return Object.values(permissions)
      .flat()
      .filter((p) => typeof p === "string" && p.includes("."));
  }
  return [];
}

const PERMISSION_LABEL_MAP = Object.fromEntries(
  OPS_PERMISSION_GROUPS.flatMap((group) =>
    group.permissions.map((item) => [item.key, item.label])
  )
);

export function getPermissionLabel(key) {
  if (!key) return "";
  return PERMISSION_LABEL_MAP[key] || String(key).replace(/[._]/g, " ");
}

export function userHasPermission(user, permission) {
  if (!permission) return true;
  if (!user) return false;
  const role = normalizeRole(user.role);
  if (role === ROLES.SUPER_ADMIN) return true;
  if (role === ROLES.EXECUTIVE_MANAGER) {
    return permission !== P.OPS_USERS_MANAGE;
  }

  const perms = user.permissions ?? getPermissionsForRole(user.role);
  if (perms.includes(permission)) return true;

  /** Backend aggregate keys (api.simcollect.com) that imply finer-grained FE keys. */
  const satisfiedByBackendKey = {
    "institutions.create": "institutions.manage",
    "institutions.update": "institutions.manage",
    "institutions.onboard": "institutions.manage",
    "institutions.invite_resend": "institutions.manage",
    "customers.update": ["customers.update", "customers.manage"],
    "contact_centre.manage": ["contact_centre.manage", "communications.manage"],
    "payments.initiate": ["payments.initiate", "payments.manage"],
    "ops_users.view": "settings.manage",
    "ops_users.invite": "settings.manage",
    "ops_users.manage": "settings.manage",
    "audit.view": ["audit.view", "dashboard.view", "settings.view"],
  };

  const alias = satisfiedByBackendKey[permission];
  if (!alias) return false;

  const aliases = Array.isArray(alias) ? alias : [alias];
  return aliases.some((key) => perms.includes(key));
}

export function userHasAnyPermission(user, permissions) {
  if (!permissions?.length) return true;
  return permissions.some((p) => userHasPermission(user, p));
}

export function userHasAllPermissions(user, permissions) {
  if (!permissions?.length) return true;
  return permissions.every((p) => userHasPermission(user, p));
}
